import React, { useEffect, useRef, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { NavLink, useNavigate } from 'react-router-dom'

import {
  CContainer,
  CDropdown,
  CDropdownItem,
  CDropdownMenu,
  CDropdownToggle,
  CHeader,
  CHeaderNav,
  CHeaderToggler,
  CNavLink,
  CNavItem,
  useColorModes,
  CBadge,
} from '@coreui/react'

import CIcon from '@coreui/icons-react'
import {
  cilBell,
  cilEnvelopeOpen,
  cilMenu,
  cilMoon,
  cilContrast,
  cilSun,
  cilSettings,
} from '@coreui/icons'

import { AppBreadcrumb } from './index'
import { BackAppHeaderDropdown } from './header/Backindex'
import { axiosAuth } from '../../../axios'
import { useToast } from './ToastContext'
import { useAuth } from '../../../hooks/useAuth'

const BackAppHeader = () => {
  const headerRef = useRef(null)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { colorMode, setColorMode } = useColorModes(
      'coreui-free-react-admin-template-theme'
  )

  const sidebarShow = useSelector((state) => state.sidebarShow)
  const { pushToast } = useToast()
  const { getAccessToken } = useAuth()

  const token = getAccessToken()

  const [notifications, setNotifications] = useState([])
  const [messages, setMessages] = useState([])

  const allowNewNotifications = localStorage.getItem('allowNewNotifications') === 'true'
  const allowNewMessages = localStorage.getItem('allowNewMessages') === 'true'
  const allowToasts = localStorage.getItem('allowToasts') === 'true'

  const notifySound = `${process.env.PUBLIC_URL}/assets/sounds/notify.wav`

  const seenNotificationsKey = 'seenNotificationsIds'
  const seenMessagesKey = 'seenMessagesIds'

  const getSeenIds = (key) => JSON.parse(localStorage.getItem(key) || '[]')
  const setSeenIds = (key, ids) =>
      localStorage.setItem(key, JSON.stringify(ids))

  const playSound = () => {
    const audio = new Audio(notifySound)
    audio.play().catch(() => {})
  }

  // ==========================
  // Fetch alerts sécurisé
  // ==========================
  const fetchAlerts = async () => {
    if (!token) return

    try {
      const res = await axiosAuth.get('/api/alerts')

      const newNotifications = res.data?.data?.notifications || []
      const newMessages = res.data?.data?.messages || []

      const seenNotifications = getSeenIds(seenNotificationsKey)
      const seenMessages = getSeenIds(seenMessagesKey)

      const freshNotifs = newNotifications.filter(
          (n) => !seenNotifications.includes(n.id)
      )
      const freshMsgs = newMessages.filter(
          (m) => !seenMessages.includes(m.id)
      )

      if (freshNotifs.length && allowNewNotifications && allowToasts) {
        pushToast(
            'Notifications',
            freshNotifs.length === 1
                ? 'Nouvelle notification reçue 🔔'
                : `${freshNotifs.length} nouvelles notifications 🔔`,
            'info'
        )
        playSound()
      }

      if (freshMsgs.length && allowNewMessages && allowToasts) {
        pushToast(
            'Messages',
            freshMsgs.length === 1
                ? 'Nouveau message reçu 📩'
                : `${freshMsgs.length} nouveaux messages 📩`,
            'success'
        )
        playSound()
      }

      setSeenIds(seenNotificationsKey, newNotifications.map((n) => n.id))
      setSeenIds(seenMessagesKey, newMessages.map((m) => m.id))

      setNotifications(newNotifications)
      setMessages(newMessages)
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.clear()
        navigate('/login')
      } else {
        console.error('Erreur récupération alertes:', error)
      }
    }
  }

  // ==========================
  // Effects
  // ==========================
  useEffect(() => {
    if (!token) return
    fetchAlerts()
    const interval = setInterval(fetchAlerts, 300000)
    return () => clearInterval(interval)
  }, [token])

  useEffect(() => {
    const onScroll = () => {
      headerRef.current?.classList.toggle(
          'shadow-sm',
          document.documentElement.scrollTop > 0
      )
    }
    document.addEventListener('scroll', onScroll)
    return () => document.removeEventListener('scroll', onScroll)
  }, [])

  return (
      <CHeader position="sticky" className="mb-4 p-0" ref={headerRef}>
        <CContainer className="border-bottom px-4" fluid>
          <CHeaderToggler
              onClick={() =>
                  dispatch({ type: 'set', sidebarShow: !sidebarShow })
              }
              style={{ marginInlineStart: '-14px' }}
          >
            <CIcon icon={cilMenu} size="lg" />
          </CHeaderToggler>

          {/* Navigation principale */}
          <CHeaderNav className="d-none d-md-flex">
            <CNavItem>
              <CNavLink as={NavLink} to="/dashboard">
                Dashboard
              </CNavLink>
            </CNavItem>
            <CNavItem>
              <CNavLink as={NavLink} to="/settings">
                Settings
              </CNavLink>
            </CNavItem>
          </CHeaderNav>

          {/* Alerts */}
          <CHeaderNav className="ms-auto">
            <CDropdown variant="nav-item" alignment="end">
              <CDropdownToggle caret={false}>
                <CIcon icon={cilBell} size="lg" />
                {!!notifications.length && (
                    <CBadge color="info" className="ms-2">
                      {notifications.length}
                    </CBadge>
                )}
              </CDropdownToggle>

              <CDropdownMenu>
                <CDropdownItem component="h6" className="dropdown-header">
                  Notifications
                </CDropdownItem>

                {!notifications.length && (
                    <CDropdownItem>Aucune notification</CDropdownItem>
                )}

                {notifications.map((notif) => (
                    <CDropdownItem
                        key={notif.id}
                        style={{ cursor: 'pointer' }}
                        onClick={async () => {
                          if (!notif.is_read) {
                            await axiosAuth.post(
                                `/api/notifications/${notif.id}/read`
                            )
                          }
                          setNotifications((prev) =>
                              prev.filter((n) => n.id !== notif.id)
                          )
                          navigate(`/dashboard/notification/${notif.id}`)
                        }}
                    >
                      {notif.title || notif.message}
                    </CDropdownItem>
                ))}

                <CDropdownItem
                    onClick={() => navigate('/dashboard/notifications')}
                    style={{ color: 'green', fontWeight: 'bold', cursor: 'pointer' }}
                >
                  Voir toutes les notifications
                </CDropdownItem>
              </CDropdownMenu>
            </CDropdown>

            {/* Messages */}
            <CDropdown variant="nav-item" alignment="end" className="ms-2">
              <CDropdownToggle caret={false}>
                <CIcon icon={cilEnvelopeOpen} size="lg" />
                {!!messages.length && (
                    <CBadge color="success" className="ms-2">
                      {messages.length}
                    </CBadge>
                )}
              </CDropdownToggle>

              <CDropdownMenu>
                <CDropdownItem component="h6" className="dropdown-header">
                  Messages
                </CDropdownItem>

                {!messages.length && (
                    <CDropdownItem>Aucun message</CDropdownItem>
                )}

                {messages.map((msg) => (
                    <CDropdownItem
                        key={msg.id}
                        style={{ cursor: 'pointer' }}
                        onClick={async () => {
                          if (!msg.is_read) {
                            await axiosAuth.post(`/api/messages/read/${msg.id}`)
                          }
                          setMessages((prev) =>
                              prev.filter((m) => m.id !== msg.id)
                          )
                          navigate(`/dashboard/message/${msg.id}`)
                        }}
                    >
                      {msg.subject || msg.content}
                    </CDropdownItem>
                ))}

                <CDropdownItem
                    onClick={() => navigate('/dashboard/messages')}
                    style={{ color: 'green', fontWeight: 'bold', cursor: 'pointer' }}
                >
                  Voir tous les messages
                </CDropdownItem>
              </CDropdownMenu>
            </CDropdown>

            <CNavItem className="ms-2">
              <CNavLink as={NavLink} to="/dashboard/parametres">
                <CIcon icon={cilSettings} size="lg" />
              </CNavLink>
            </CNavItem>
          </CHeaderNav>

          {/* Thème + User */}
          <CHeaderNav>
            <li className="nav-item py-1">
              <div className="vr h-100 mx-2" />
            </li>

            <CDropdown variant="nav-item" placement="bottom-end">
              <CDropdownToggle caret={false}>
                {colorMode === 'dark' ? (
                    <CIcon icon={cilMoon} size="lg" />
                ) : colorMode === 'auto' ? (
                    <CIcon icon={cilContrast} size="lg" />
                ) : (
                    <CIcon icon={cilSun} size="lg" />
                )}
              </CDropdownToggle>

              <CDropdownMenu>
                <CDropdownItem as="button" onClick={() => setColorMode('light')}>
                  <CIcon icon={cilSun} className="me-2" /> Light
                </CDropdownItem>
                <CDropdownItem as="button" onClick={() => setColorMode('dark')}>
                  <CIcon icon={cilMoon} className="me-2" /> Dark
                </CDropdownItem>
                <CDropdownItem as="button" onClick={() => setColorMode('auto')}>
                  <CIcon icon={cilContrast} className="me-2" /> Auto
                </CDropdownItem>
              </CDropdownMenu>
            </CDropdown>

            <li className="nav-item py-1">
              <div className="vr h-100 mx-2" />
            </li>

            <BackAppHeaderDropdown />
          </CHeaderNav>
        </CContainer>

        <CContainer fluid className="px-4">
          <AppBreadcrumb />
        </CContainer>
      </CHeader>
  )
}

export default BackAppHeader
